// database.js
//
// System Health → Database screen (plan Task 5.2). Atomic extraction of THREE
// stacked cards out of the old dashboard.js — Query Observability
// (`renderQueryObservability`), Slow Queries (`renderSlowQueries`), and Table
// Sizes (`renderTableSizes`) — into one screen, preserving every id
// (`admin-qobs-*`, `admin-sq-*`, `admin-ts-*`) and every action (threshold
// config, clear buffer, per-section refresh).
//
// Lifecycle: the Task 3.1 Step-3c idiom with a SEPARATE generation lane PER
// independently-refreshable section — `qobsGen`, `slowGen`, `tablesGen`.
//
//   * The INITIAL load is ONE operation (one `op.signal` + one deadline) that
//     bumps ALL THREE section generations and passes the IDENTICAL signal to all
//     three initial GETs. Each section's result is committed INDEPENDENTLY
//     (generation-guarded per-promise), so a slow or failed section never blanks
//     another. An abort sweep renders a terminal timeout for any section still
//     unsettled when the deadline fires (a signal-ignoring fetch still recovers).
//
//   * Each LATER refresh/action is a NEW per-section operation that bumps ONLY
//     its section's generation. Query-observability and table-sizes refresh
//     independently.
//
//   * The ENTIRE slow-query lane — refresh + clear + config — shares ONE mutex
//     (`slowBusy`) plus the `slowGen` generation: while ANY of the three is in
//     flight, ALL three controls are disabled AND a forced/programmatic overlap
//     bails, so a Refresh can never start during a clear/config and supersede its
//     authoritative reload, and clear/config can never race each other.
//
// Actions are FAIL-CLOSED (same model as feature toggles): a definitive server
// rejection (4xx) re-enables cleanly and reports the error; a TIMEOUT / network /
// 5xx, a response-contract failure (clear `{ok:false}` / config wrong-echo), OR a
// mutation-applied-but-reload-failed outcome is INDETERMINATE — surface
// "couldn't confirm — reload" and do NOT present the section's data as current.
//
// Every response (initial GET, refresh GET, AND each action stage) is validated
// with the shared `validateShape`/`toError` (apps/admin/src/lib/admin-validate.js)
// BEFORE any render. All three tables are high-cardinality, so each renders a
// row cap with a "showing N of M" note.

import { api } from '../api.js';
import { createOperation, createGeneration, whenAborted, navAbort, abortErr } from '../lib/admin-lifecycle.js';
import { validateShape, toError } from '../lib/admin-validate.js';
import { escapeHtml } from '../../../../src/shared/escape-html.js';
import { formatBytes } from '../lib/format.js';

// GET endpoints. QOBS_EP carries the fixed query the screen loads with; the
// shared `validateShape` normalizes the query string back to the base path.
export const QOBS_EP = '/api/admin/query-observability?days=7&limit=50';
export const SLOW_EP = '/api/admin/slow-queries';
export const TABLES_EP = '/api/admin/table-sizes';
const CLEAR_EP = '/api/admin/slow-queries/clear';
const CONFIG_EP = '/api/admin/slow-queries/config';

// Rendered-row caps (spec §3.9 — all three tables are high-cardinality).
export const QOBS_QUERY_CAP = 50;
export const QOBS_ROUTE_CAP = 100;
export const SLOW_CAP = 100;
export const TABLES_CAP = 50;

const INDETERMINATE_MSG = "Couldn't confirm. Reload the page.";

// ── Moved helpers (verbatim from dashboard.js) ───────────────────────────

const TS_HIGHLIGHT = new Set([
  'play_heartbeats', 'page_views', 'achievement_events',
  'netplay_metrics', 'play_sessions',
]);

// formatBytes now lives in ../lib/format.js (was duplicated with dashboard.js); re-exported for importers (e.g. tests).
export { formatBytes };

export function compactNumber(value) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function formatTime(iso) {
  try {
    const d = new Date(iso);
    // toLocaleTimeString on an Invalid Date returns the literal "Invalid Date"
    // rather than throwing, so guard explicitly and fall back to an em dash.
    if (Number.isNaN(d.getTime())) return '–';
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '–';
  }
}

export function collapseLongInClauses(sql) {
  if (!sql) return '';
  return sql.replace(/\bIN\s*\((\$\d+(?:\s*,\s*\$\d+){10,})\)/gi, (match, inner) => {
    const params = inner.split(',');
    const first = params[0].trim();
    const last = params[params.length - 1].trim();
    return `IN (${first} .. ${last} · ${params.length} params)`;
  });
}

export function durationClass(ms) {
  if (ms >= 500) return 'admin-sq-dur-red';
  if (ms >= 200) return 'admin-sq-dur-yellow';
  return 'admin-sq-dur-green';
}

// ── Structured-error → user text (keyed on kind, per section) ─────────────

function describeError(section, err) {
  if (!err) return `${section} unavailable. Reload the page.`;
  switch (err.kind) {
    case 'shape': return `${section} unavailable: the response was malformed. Reload the page.`;
    case 'timeout': return `Couldn't load ${section}: request timed out.`;
    case 'cancelled': return 'Cancelled.';
    case 'network': return `Couldn't load ${section}: network error.`;
    case 'http': return `Couldn't load ${section}: server error (${err.status}).`;
    default: return `Couldn't load ${section}: ${err.cause?.message || 'unknown error'}.`;
  }
}

// A definitive non-apply is a client-side (4xx) rejection: the server refused the
// request, so the mutation provably did NOT apply → safe to re-enable cleanly.
// Everything else (5xx / network / timeout / contract failure / reload failure)
// is INDETERMINATE — fail closed, never claim the data is current.
// NOTE: deliberately BROADER than feature-flags.js's isPreApplyRejection (only
// 400/403) — the slow-query clear/config actions treat any 4xx as a provable
// non-apply; do not "unify" the two.
function is4xxNonApply(err) {
  return !!err && err.kind === 'http' && err.status >= 400 && err.status < 500;
}


// ── Screen ───────────────────────────────────────────────────────────────

export function renderDatabase(params, ctx) {
  const root = document.querySelector('[data-screen="database"]');
  if (!root) return undefined;

  const el = (id) => root.querySelector(`#${id}`);

  // Query Observability elements.
  const qobsQueryBody = el('admin-qobs-query-body');
  const qobsRouteBody = el('admin-qobs-route-body');
  const qobsStatsEl = el('admin-qobs-stats');
  const qobsWarnings = el('admin-qobs-warnings');
  const qobsStatus = el('admin-qobs-status');
  const qobsRefreshBtn = el('admin-qobs-refresh');
  const qobsQueryCapNote = el('admin-qobs-query-cap-note');
  const qobsRouteCapNote = el('admin-qobs-route-cap-note');

  // Slow Queries elements.
  const slowBody = el('admin-sq-body');
  const slowStatsEl = el('admin-sq-stats');
  const slowThresholdInput = el('admin-sq-threshold');
  const slowStatus = el('admin-sq-status');
  const slowRefreshBtn = el('admin-sq-refresh');
  const slowClearBtn = el('admin-sq-clear');
  const slowThresholdBtn = el('admin-sq-threshold-btn');
  const slowCapNote = el('admin-sq-cap-note');

  // Table Sizes elements.
  const tsBody = el('admin-ts-body');
  const tsRefreshBtn = el('admin-ts-refresh');
  const tsCapNote = el('admin-ts-cap-note');

  // If the core bodies are missing the screen scaffold is absent — bail.
  if (!qobsQueryBody || !slowBody || !tsBody) return undefined;

  // Three independent state lanes (Step 3c). Every operation bumps its lane on entry.
  const qobsGen = createGeneration();
  const slowGen = createGeneration();
  const tablesGen = createGeneration();

  // Slow-lane exclusive mutex — one in-flight write per lane. The disabled
  // controls are the UI reflection; this flag is the actual guard against a
  // forced/programmatic overlap superseding an applied mutation's reload.
  let slowBusy = false;

  const offs = [];
  const on = (target, ev, fn) => { if (!target) return; target.addEventListener(ev, fn); offs.push(() => target.removeEventListener(ev, fn)); };

  // ONE persistent teardown: invalidate every lane, dispose listeners. Stays
  // registered until navigation; operations never unregister it (each registers
  // its OWN separate abort callback).
  ctx.onTeardown(() => {
    const errors = [];
    try { qobsGen.bump(); slowGen.bump(); tablesGen.bump(); } catch (e) { errors.push(e); }
    for (const off of offs) { try { off(); } catch (e) { errors.push(e); } }
    if (errors.length) return { ok: false, errors };
    return undefined;
  });

  const setSlowStatus = (text, cls = '') => {
    if (slowStatus) { slowStatus.textContent = text; slowStatus.className = `admin-status${cls ? ' ' + cls : ''}`; }
  };
  const setQobsStatus = (text, cls = '') => {
    if (qobsStatus) { qobsStatus.textContent = text; qobsStatus.className = `admin-status${cls ? ' ' + cls : ''}`; }
  };

  // ── Loading placeholders ────────────────────────────────────────────────

  function setQobsLoading() {
    qobsQueryBody.innerHTML = '<tr><td colspan="6" class="admin-loading">Loading…</td></tr>';
    qobsRouteBody.innerHTML = '<tr><td colspan="9" class="admin-loading">Loading…</td></tr>';
    setQobsStatus('', '');
  }
  function setSlowLoading() {
    slowBody.innerHTML = '<tr><td colspan="5" class="admin-loading">Loading…</td></tr>';
  }
  function setTablesLoading() {
    tsBody.innerHTML = '<tr><td colspan="3" class="admin-loading">Loading…</td></tr>';
  }

  // ── Query Observability rendering ───────────────────────────────────────

  function renderQobs(data) {
    const pool = data.pool || {};
    const coverage = data.coverage || {};
    if (qobsStatsEl) {
      qobsStatsEl.innerHTML =
        `<span class="admin-sq-stat">Coverage <strong>${coverage.sampledHours || 0}h${coverage.complete ? '' : ' (warming)'}</strong></span>` +
        `<span class="admin-sq-stat">RW pool <strong>${pool.rw?.total || 0} total / ${pool.rw?.waiting || 0} waiting</strong></span>` +
        `<span class="admin-sq-stat">RO pool <strong>${pool.readonlyConfigured ? `${pool.ro?.total || 0} total / ${pool.ro?.waiting || 0} waiting` : 'not configured'}</strong></span>` +
        `<span class="admin-sq-stat">Replica <strong>${escapeHtml((data.replicaId || 'unknown').slice(0, 16))}</strong></span>`;
    }

    const alertMessages = (data.warnings || []).map((warning) => warning.message);
    if (!data.available) alertMessages.unshift(`Durable query stats unavailable: ${data.reason || 'unknown reason'}.`);
    if (qobsWarnings) {
      qobsWarnings.hidden = alertMessages.length === 0;
      qobsWarnings.innerHTML = alertMessages.map((message) => `<div>${escapeHtml(message)}</div>`).join('');
    }
    setQobsStatus('', '');

    // Query table (cap 50).
    if (!data.available) {
      qobsQueryBody.innerHTML = '<tr><td colspan="6" class="admin-loading">Apply migration 0112 and enable pg_stat_statements to begin collection.</td></tr>';
      setCapNote(qobsQueryCapNote, 0, QOBS_QUERY_CAP);
    } else if (!data.queries.length) {
      qobsQueryBody.innerHTML = '<tr><td colspan="6" class="admin-loading">No complete counter intervals yet. A second hourly sample will produce the first deltas.</td></tr>';
      setCapNote(qobsQueryCapNote, 0, QOBS_QUERY_CAP);
    } else {
      setCapNote(qobsQueryCapNote, data.queries.length, QOBS_QUERY_CAP);
      qobsQueryBody.innerHTML = '';
      for (const query of data.queries.slice(0, QOBS_QUERY_CAP)) {
        const row = document.createElement('tr');
        row.innerHTML =
          `<td class="admin-user-num">${compactNumber(query.calls)}</td>` +
          `<td class="admin-sq-cell-dur">${compactNumber(query.total_exec_time_ms)}ms</td>` +
          `<td class="admin-sq-cell-dur">${compactNumber(query.mean_exec_time_ms)}ms</td>` +
          `<td class="admin-user-num">${compactNumber(query.rows)}</td>` +
          `<td class="admin-user-num">${compactNumber(query.shared_blks_hit + query.local_blks_hit)} / ${compactNumber(query.shared_blks_read + query.local_blks_read)} / ${compactNumber(query.temp_blks_read + query.temp_blks_written)}</td>` +
          `<td class="admin-sq-cell-sql">${escapeHtml(collapseLongInClauses(query.query))}</td>`;
        qobsQueryBody.appendChild(row);
      }
    }

    // Route table (cap 100).
    const routes = data.routeMetrics || [];
    if (!routes.length) {
      qobsRouteBody.innerHTML = '<tr><td colspan="9" class="admin-loading">No route samples captured yet.</td></tr>';
      setCapNote(qobsRouteCapNote, 0, QOBS_ROUTE_CAP);
    } else {
      setCapNote(qobsRouteCapNote, routes.length, QOBS_ROUTE_CAP);
      qobsRouteBody.innerHTML = '';
      for (const metric of routes.slice(0, QOBS_ROUTE_CAP)) {
        const row = document.createElement('tr');
        row.innerHTML =
          `<td class="admin-sq-cell-time">${formatTime(metric.timestamp)}</td>` +
          `<td class="admin-sq-cell-route">${escapeHtml(metric.route)}</td>` +
          `<td class="admin-sq-cell-dur ${durationClass(metric.durationMs)}">${metric.durationMs}ms</td>` +
          `<td class="admin-user-num">${metric.status}</td>` +
          `<td class="admin-user-num">${metric.queryCount}</td>` +
          `<td>${escapeHtml(metric.cacheResult)}</td>` +
          `<td>${escapeHtml((metric.pools || []).join(', ') || '–')}</td>` +
          `<td class="admin-user-num">${metric.poolWaiting}</td>` +
          `<td class="admin-user-id">${escapeHtml((metric.replicaId || '').slice(0, 8))}</td>`;
        qobsRouteBody.appendChild(row);
      }
    }
  }

  function renderQobsError(err) {
    const message = describeError('query observability', err);
    qobsQueryBody.innerHTML = `<tr><td colspan="6" class="admin-loading">Error: ${escapeHtml(message)}</td></tr>`;
    qobsRouteBody.innerHTML = '<tr><td colspan="9" class="admin-loading">Unavailable.</td></tr>';
    if (qobsStatsEl) qobsStatsEl.innerHTML = '';
    if (qobsWarnings) { qobsWarnings.hidden = true; qobsWarnings.innerHTML = ''; }
    setCapNote(qobsQueryCapNote, 0, QOBS_QUERY_CAP);
    setCapNote(qobsRouteCapNote, 0, QOBS_ROUTE_CAP);
    setQobsStatus(`Error: ${message}`, 'error');
  }

  // ── Slow Queries rendering ──────────────────────────────────────────────

  function renderSlow(data) {
    const { queries, stats } = data;
    if (slowStatsEl) {
      slowStatsEl.innerHTML =
        `<span class="admin-sq-stat">p50 <strong>${stats.p50}ms</strong></span>` +
        `<span class="admin-sq-stat">p95 <strong>${stats.p95}ms</strong></span>` +
        `<span class="admin-sq-stat">p99 <strong>${stats.p99}ms</strong></span>` +
        `<span class="admin-sq-stat">Tracked <strong>${stats.totalTracked}/${stats.bufferCapacity}</strong></span>` +
        `<span class="admin-sq-stat">Total <strong>${stats.totalQueriesSeen.toLocaleString()}</strong></span>`;
    }

    // Don't clobber a value the admin is mid-edit (never rely on the `:focus`
    // pseudo, which is unreliable outside a real browser).
    if (slowThresholdInput && document.activeElement !== slowThresholdInput) {
      slowThresholdInput.value = stats.threshold;
    }

    if (!queries.length) {
      setCapNote(slowCapNote, 0, SLOW_CAP);
      slowBody.innerHTML = '<tr><td colspan="5" class="admin-loading">No slow queries captured yet.</td></tr>';
      return;
    }

    setCapNote(slowCapNote, queries.length, SLOW_CAP);
    slowBody.innerHTML = '';
    for (const q of queries.slice(0, SLOW_CAP)) {
      const row = document.createElement('tr');
      row.className = 'admin-sq-row';
      const durCls = durationClass(q.durationMs);
      row.innerHTML =
        `<td class="admin-sq-cell-time">${formatTime(q.timestamp)}</td>` +
        `<td class="admin-sq-cell-route" title="${escapeHtml(q.route || '–')}">${escapeHtml(q.route || '–')}</td>` +
        `<td class="admin-sq-cell-dur ${durCls}">${q.durationMs}ms</td>` +
        `<td class="admin-sq-cell-pool">${escapeHtml(q.pool || '–')}</td>` +
        `<td class="admin-sq-cell-sql">${escapeHtml(collapseLongInClauses(q.sql))}</td>`;
      slowBody.appendChild(row);
    }
  }

  // A refresh/load failure wipes the slow section to ONE structured error row.
  // (Mutation-applied-but-uncertain outcomes do NOT call this — they preserve the
  // last-known table and only surface the "couldn't confirm" status.)
  function renderSlowError(err) {
    const message = describeError('slow queries', err);
    if (slowStatsEl) slowStatsEl.innerHTML = '';
    setCapNote(slowCapNote, 0, SLOW_CAP);
    slowBody.innerHTML = `<tr><td colspan="5" class="admin-loading">Error: ${escapeHtml(message)}</td></tr>`;
    setSlowStatus(`Error: ${message}`, 'error');
  }

  // ── Table Sizes rendering ───────────────────────────────────────────────

  function renderTables(data) {
    const { tables } = data;
    if (!tables.length) {
      setCapNote(tsCapNote, 0, TABLES_CAP);
      tsBody.innerHTML = '<tr><td colspan="3" class="admin-loading">No tables found.</td></tr>';
      return;
    }
    setCapNote(tsCapNote, tables.length, TABLES_CAP);
    tsBody.innerHTML = '';
    for (const t of tables.slice(0, TABLES_CAP)) {
      const row = document.createElement('tr');
      const count = Number(t.row_count);
      const cls = TS_HIGHLIGHT.has(t.name)
        ? (count >= 1_000_000 ? 'admin-ts-count-red' : count >= 100_000 ? 'admin-ts-count-yellow' : 'admin-ts-count-green')
        : '';
      row.innerHTML =
        `<td class="admin-ts-cell-name">${escapeHtml(t.name)}${t.is_hypertable ? ' <span class="admin-ts-badge">TS</span>' : ''}</td>` +
        `<td class="admin-ts-cell-count ${cls}">${count.toLocaleString()}</td>` +
        `<td class="admin-ts-cell-count">${formatBytes(Number(t.total_bytes) || 0)}</td>`;
      tsBody.appendChild(row);
    }
  }

  function renderTablesError(err) {
    setCapNote(tsCapNote, 0, TABLES_CAP);
    tsBody.innerHTML = `<tr><td colspan="3" class="admin-loading">Error: ${escapeHtml(describeError('table sizes', err))}</td></tr>`;
  }

  function setCapNote(noteEl, total, cap) {
    if (!noteEl) return;
    const capped = total > cap;
    noteEl.hidden = !capped;
    noteEl.textContent = capped ? `showing ${cap} of ${total}` : '';
  }

  // ── Initial load — ONE operation, ONE signal, ONE deadline ──────────────
  // Bumps all three section generations; commits each section independently.

  function initialLoad() {
    // On ENTRY, reset every ACTION control to its enabled baseline. The router
    // only toggles `hidden`, so DOM + control state persist across navigation,
    // and a worker's `finally` restore is SKIPPED once its generation went stale
    // on navigate-away — so a click → navigate-while-in-flight → return would
    // otherwise leave that control `disabled=true` forever (only a full reload
    // recovered it). Re-enabling here is safe: this is a FRESH screen instance
    // (fresh generations, slowBusy=false) and the departed screen's op was
    // already aborted + its generations bumped by teardown, so nothing is truly
    // in flight. This does NOT weaken the in-flight mutex — an action started on
    // THIS instance still disables + gates via slowBusy/slowGen exactly as before.
    [slowRefreshBtn, slowClearBtn, slowThresholdBtn, qobsRefreshBtn, tsRefreshBtn]
      .forEach((b) => { if (b) b.disabled = false; });

    const qTok = qobsGen.bump();
    const sTok = slowGen.bump();
    const tTok = tablesGen.bump();

    const sections = [
      { key: 'qobs', gen: qobsGen, token: qTok, ep: QOBS_EP, render: renderQobs, renderErr: renderQobsError },
      { key: 'slow', gen: slowGen, token: sTok, ep: SLOW_EP, render: renderSlow, renderErr: renderSlowError },
      { key: 'tables', gen: tablesGen, token: tTok, ep: TABLES_EP, render: renderTables, renderErr: renderTablesError },
    ];

    setQobsLoading();
    setSlowLoading();
    setTablesLoading();

    const op = createOperation();
    const unregister = ctx.onTeardown(() => op.abort(navAbort()));
    let abortWait = null;

    // A section is SEALED once committed, failed, or swept — nothing else writes it.
    const settled = new Set();

    const commit = (sec) => (res) => {
      if (settled.has(sec.key)) return;
      settled.add(sec.key);
      if (sec.gen.isStale(sec.token)) return;                          // navigation/supersession → nothing
      if (op.closed || op.signal.aborted) {                            // deadline fired before this resolve → timeout
        sec.renderErr(toError(sec.ep, op.signal.reason ?? abortErr()).error);
        return;
      }
      try {
        const v = validateShape(sec.ep, res);
        if (v.ok) sec.render(res); else sec.renderErr(v.error);
      } catch (e) {
        sec.renderErr({ kind: 'unknown', endpoint: sec.ep, status: null, cause: e });
      }
    };
    const fail = (sec) => (e) => {
      if (settled.has(sec.key)) return;
      settled.add(sec.key);
      if (sec.gen.isStale(sec.token)) return;                          // navigation → nothing
      sec.renderErr(toError(sec.ep, e).error);
    };
    // On abort, finalize every section that never settled. `toError` preserves the
    // reason (deadline → timeout; navigation → cancelled). A stale gen (navigation)
    // seals the section WITHOUT rendering; a live gen (deadline) renders the timeout.
    const onAbortSweep = () => {
      for (const sec of sections) {
        if (settled.has(sec.key)) continue;
        settled.add(sec.key);
        if (sec.gen.isStale(sec.token)) continue;
        sec.renderErr(toError(sec.ep, op.signal.reason ?? abortErr()).error);
      }
    };
    op.signal.addEventListener('abort', onAbortSweep, { once: true });
    const removeAbortSweep = () => op.signal.removeEventListener('abort', onAbortSweep);

    return (async () => {
      try {
        abortWait = whenAborted(op.signal);
        const promises = sections.map((sec) =>
          api.get(sec.ep, { signal: op.signal }).then(commit(sec), fail(sec)));
        await Promise.race([Promise.allSettled(promises), abortWait.promise]).catch(() => {});
      } finally {
        try { removeAbortSweep(); } catch { /* once:true already removed on abort */ }
        if (abortWait) abortWait.cancel();
        op.finish();
        unregister();
      }
    })();
  }

  // ── Per-section refresh (query-observability / table-sizes) ─────────────
  // Bumps ONLY its own generation; disables ONLY its own refresh control.

  async function runSectionRefresh({ gen, ep, setLoading, render, renderErr, control }) {
    const token = gen.bump();
    const baseline = control ? control.disabled : false;
    if (control) control.disabled = true;
    setLoading();
    const op = createOperation();
    const unregister = ctx.onTeardown(() => op.abort(navAbort()));
    let abortWait = null;
    try {
      abortWait = whenAborted(op.signal);
      let resp;
      try {
        resp = await Promise.race([api.get(ep, { signal: op.signal }), abortWait.promise]);
      } catch (err) {
        if (gen.isStale(token)) return;
        renderErr(toError(ep, err).error);
        return;
      }
      if (gen.isStale(token)) return;
      if (op.closed || op.signal.aborted) { renderErr(toError(ep, op.signal.reason ?? abortErr()).error); return; }
      try {
        const v = validateShape(ep, resp);
        if (v.ok) render(resp); else renderErr(v.error);
      } catch (e) {
        // A validate/render throw on shape-valid data is a code bug — surface it as a
        // structured 'unknown' section error rather than letting it escape this
        // fire-and-forget refresh as an unhandled rejection (matches the initialLoad
        // commit + the dashboard/rooms/slow-lane loaders).
        renderErr({ kind: 'unknown', endpoint: ep, status: null, cause: e });
      }
    } finally {
      if (abortWait) abortWait.cancel();
      op.finish();
      unregister();
      if (control && !gen.isStale(token)) control.disabled = baseline;
    }
  }

  const refreshQobs = () => runSectionRefresh({
    gen: qobsGen, ep: QOBS_EP, setLoading: setQobsLoading, render: renderQobs, renderErr: renderQobsError, control: qobsRefreshBtn,
  });
  const refreshTables = () => runSectionRefresh({
    gen: tablesGen, ep: TABLES_EP, setLoading: setTablesLoading, render: renderTables, renderErr: renderTablesError, control: tsRefreshBtn,
  });

  // ── Slow-query lane (refresh + clear + config share ONE mutex) ──────────

  const slowControls = () => [slowRefreshBtn, slowClearBtn, slowThresholdBtn].filter(Boolean);

  // Acquire the exclusive lane, disable ALL three controls, run `worker`, then
  // restore controls to their captured baseline and release. A forced overlap
  // (buttons re-enabled programmatically) BAILS while the lane is busy, so an
  // applied mutation's authoritative reload is never superseded.
  async function runSlowLane(worker) {
    if (slowBusy) return;                              // hard mutex — one in-flight lane write
    slowBusy = true;
    const controls = slowControls();
    const baselines = controls.map((c) => c.disabled);
    const token = slowGen.bump();
    controls.forEach((c) => { c.disabled = true; });
    const op = createOperation();
    const unregister = ctx.onTeardown(() => op.abort(navAbort()));
    let abortWait = null;
    try {
      abortWait = whenAborted(op.signal);
      // Propagate the worker's outcome (structured indeterminate / {ok:true}) so
      // it is reachable end-to-end; the click handlers ignore it (fire-and-forget).
      return await worker(op, token, abortWait);
    } catch (err) {
      // Unexpected orchestration error → indeterminate presentation (lane token only).
      if (!slowGen.isStale(token)) setSlowStatus(INDETERMINATE_MSG, 'error');
    } finally {
      if (abortWait) abortWait.cancel();
      op.finish();
      unregister();
      // Restore EACH control to its captured baseline (lane token only, so the
      // current screen recovers after a timeout; navigation suppresses via the
      // bumped generation).
      if (!slowGen.isStale(token)) controls.forEach((c, i) => { c.disabled = baselines[i]; });
      slowBusy = false;
    }
  }

  // Refresh: read-only re-GET of the slow-query section.
  async function slowRefreshWorker(op, token, abortWait) {
    setSlowLoading();
    setSlowStatus('', '');
    let resp;
    try {
      resp = await Promise.race([api.get(SLOW_EP, { signal: op.signal }), abortWait.promise]);
    } catch (err) {
      if (slowGen.isStale(token)) return;
      renderSlowError(toError(SLOW_EP, err).error);
      return;
    }
    if (slowGen.isStale(token)) return;
    if (op.closed || op.signal.aborted) { renderSlowError(toError(SLOW_EP, op.signal.reason ?? abortErr()).error); return; }
    const v = validateShape(SLOW_EP, resp);
    if (v.ok) renderSlow(resp); else renderSlowError(v.error);
  }

  // Build + surface the mandated structured INDETERMINATE result (plan line 1234):
  // an uncertain clear/config outcome must preserve BOTH stage responses —
  // `applied` (the successful action response, when the action itself succeeded)
  // and `reload` (the authoritative-reload body, when one was RECEIVED) — next to
  // the `error`, so the outcome is never a single lossy `{ok:false,error}`. Sets
  // the user-facing "couldn't confirm — reload" status (the last-known table is
  // PRESERVED — an indeterminate outcome never wipes/re-renders the section),
  // logs the structured object for diagnostics, and RETURNS it. The click
  // handlers are fire-and-forget (they ignore the return); the return + log exist
  // for tests + observability.
  function slowIndeterminate(action, parts) {
    setSlowStatus(INDETERMINATE_MSG, 'error');
    const result = { ok: false, ...parts };
    console.error(`[admin/database] ${action} indeterminate`, result);
    return result;
  }

  // Clear: POST clear → validate `{ok:true}` → authoritative reload GET → render.
  // Fail-closed: `ok !== true` (contract failure) and any reload failure are
  // INDETERMINATE; a 4xx is a definitive clean rejection.
  async function slowClearWorker(op, token, abortWait) {
    setSlowStatus('Clearing…', 'saving');
    let clearResp;
    try {
      clearResp = await Promise.race([api.post(CLEAR_EP, undefined, { signal: op.signal }), abortWait.promise]);
    } catch (err) {
      if (slowGen.isStale(token)) return;
      const e = toError(CLEAR_EP, err).error;
      // A definitive 4xx provably did NOT apply → clean error, not indeterminate.
      if (is4xxNonApply(e)) { setSlowStatus(`Error: ${describeError('slow queries', e)}`, 'error'); return { ok: false, error: e }; }
      return slowIndeterminate('clear', { error: e });
    }
    if (slowGen.isStale(token)) return;
    if (op.closed || op.signal.aborted) {
      return slowIndeterminate('clear', { error: toError(CLEAR_EP, op.signal.reason ?? abortErr()).error });
    }
    const cv = validateShape(CLEAR_EP, clearResp);
    if (!cv.ok || clearResp?.ok !== true) {
      // Contract failure — a shape-valid `{ok:false}` is NOT a successful clear.
      const error = cv.ok
        ? { kind: 'contract', endpoint: CLEAR_EP, status: null, message: 'clear did not confirm (ok !== true)', cause: null }
        : cv.error;
      return slowIndeterminate('clear', { error });
    }
    return slowAuthoritativeReload(op, token, abortWait, 'clear', 'Buffer cleared.', clearResp);
  }

  // Config: PATCH `{threshold}` → validate `{threshold:num}` echoes the requested
  // value → authoritative reload → render. Same fail-closed classification.
  async function slowConfigWorker(op, token, abortWait, value) {
    setSlowStatus('Saving…', 'saving');
    let cfgResp;
    try {
      cfgResp = await Promise.race([api.patch(CONFIG_EP, { threshold: value }, { signal: op.signal }), abortWait.promise]);
    } catch (err) {
      if (slowGen.isStale(token)) return;
      const e = toError(CONFIG_EP, err).error;
      // A definitive 4xx provably did NOT apply → clean error, not indeterminate.
      if (is4xxNonApply(e)) { setSlowStatus(`Error: ${describeError('slow queries', e)}`, 'error'); return { ok: false, error: e }; }
      return slowIndeterminate('config', { error: e });
    }
    if (slowGen.isStale(token)) return;
    if (op.closed || op.signal.aborted) {
      return slowIndeterminate('config', { error: toError(CONFIG_EP, op.signal.reason ?? abortErr()).error });
    }
    const cv = validateShape(CONFIG_EP, cfgResp);
    if (!cv.ok || cfgResp?.threshold !== value) {
      // Contract failure / wrong echo → INDETERMINATE, do NOT proceed.
      const error = cv.ok
        ? { kind: 'contract', endpoint: CONFIG_EP, status: null, message: `config echo mismatch (got ${cfgResp?.threshold}, want ${value})`, cause: null }
        : cv.error;
      return slowIndeterminate('config', { error });
    }
    return slowAuthoritativeReload(op, token, abortWait, 'config', 'Threshold updated.', cfgResp);
  }

  // Shared stage 2 of clear/config: the authoritative reload. A reload failure
  // AFTER an applied mutation is INDETERMINATE (never "saved", never wiped). Each
  // indeterminate exit builds the structured result carrying `applied` (the
  // action response that succeeded) plus `reload` when a reload body was received.
  async function slowAuthoritativeReload(op, token, abortWait, action, successMsg, applied) {
    let reloadResp;
    try {
      reloadResp = await Promise.race([api.get(SLOW_EP, { signal: op.signal }), abortWait.promise]);
    } catch (err) {
      if (slowGen.isStale(token)) return;
      // Mutation applied, reload FAILED (no body received) → preserve the table.
      return slowIndeterminate(action, { applied, error: toError(SLOW_EP, err).error });
    }
    if (slowGen.isStale(token)) return;
    if (op.closed || op.signal.aborted) {
      return slowIndeterminate(action, { applied, reload: reloadResp, error: toError(SLOW_EP, op.signal.reason ?? abortErr()).error });
    }
    const rv = validateShape(SLOW_EP, reloadResp);
    if (!rv.ok) {
      // Reload body received but malformed → indeterminate, keep the last-known table.
      return slowIndeterminate(action, { applied, reload: reloadResp, error: rv.error });
    }
    renderSlow(reloadResp);
    setSlowStatus(successMsg, 'success');
    return { ok: true, applied, reload: reloadResp };
  }

  // ── Listeners (static controls — bound once, survive re-render) ──────────

  on(qobsRefreshBtn, 'click', () => { refreshQobs(); });
  on(tsRefreshBtn, 'click', () => { refreshTables(); });
  on(slowRefreshBtn, 'click', () => { runSlowLane(slowRefreshWorker); });
  on(slowClearBtn, 'click', () => { runSlowLane(slowClearWorker); });
  on(slowThresholdBtn, 'click', () => {
    const value = parseInt(slowThresholdInput?.value, 10);
    if (!value || value < 1) return;                  // invalid input never touches the lane
    runSlowLane((op, token, abortWait) => slowConfigWorker(op, token, abortWait, value));
  });

  return initialLoad();
}
